import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  Skeleton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  VideoLibrary as RecordingIcon,
  Refresh as RefreshIcon,
  UploadFile as UploadIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";
import { recordingStatusChip as statusChip } from "./tutorStatusChips";

const MAX_MB = 2048;

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.round(seconds / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m} min`;
};

/**
 * Class recordings for the groups this tutor leads.
 *
 * Hosts auto-start a local (device) recording in the meeting tab; after class they upload that
 * file (or a screen capture) here. Upload goes **straight from the browser to Cloudflare Stream**
 * via a one-time direct-upload URL minted by the server — it never passes through Laravel.
 * On self-hosted Jitsi with Jibri, cloud/Google Drive recordings can be downloaded and uploaded
 * the same way.
 *
 * Once Cloudflare finishes encoding, `refreshStatus` flips the row to `ready` and notifies the
 * group exactly once, on that transition. Playback for students is a signed, expiring URL.
 */
export default function TutorRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadingFor, setUploadingFor] = useState(null);
  const [progress, setProgress] = useState(0);

  // One hidden input, retargeted per class — simpler than one per row.
  const fileInputRef = useRef(null);
  const pendingClassRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rec, cls] = await Promise.allSettled([
        api.get("tutor/recordings"),
        api.get("tutor/classrooms"),
      ]);
      if (rec.status === "rejected") throw rec.reason;
      setRecordings(Array.isArray(rec.value.data?.data) ? rec.value.data.data : []);
      setClasses(
        cls.status === "fulfilled" && Array.isArray(cls.value.data?.data) ? cls.value.data.data : []
      );
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your recordings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll only while something is actually encoding, and stop as soon as nothing is.
  const encoding = useMemo(
    () => recordings.filter((r) => r.status === "uploading" || r.status === "processing"),
    [recordings]
  );

  useEffect(() => {
    if (encoding.length === 0) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const results = await Promise.all(
          encoding.map((r) => api.get(`tutor/recordings/${r.id}/status`).catch(() => null))
        );
        const byId = new Map();
        results.forEach((res) => {
          const row = res?.data?.data;
          if (row?.id) byId.set(row.id, row);
        });
        if (byId.size > 0) {
          setRecordings((prev) => prev.map((r) => byId.get(r.id) || r));
        }
      } catch {
        // Polling is best-effort; the Refresh button is always available.
      }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [encoding]);

  // Group classes that have finished and have no recording yet.
  const recordedClassIds = useMemo(
    () => new Set(recordings.map((r) => r.class?.id).filter(Boolean)),
    [recordings]
  );

  const uploadable = useMemo(
    () => classes.filter((c) => c.group?.id && c.status === "ended" && !recordedClassIds.has(c.id)),
    [classes, recordedClassIds]
  );

  const pickFile = (classroom) => {
    pendingClassRef.current = classroom;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const classroom = pendingClassRef.current;
    if (!file || !classroom) return;

    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`That file is larger than ${MAX_MB} MB.`);
      return;
    }

    setUploadingFor(classroom.id);
    setProgress(0);
    try {
      // 1. Ask the server for a one-time Cloudflare Stream upload slot. This is where the
      //    lead-tutor and "class belongs to a group" rules are enforced.
      const slotRes = await api.post(`tutor/classrooms/${classroom.id}/recordings`);
      const slot = slotRes.data?.data;
      if (!slot?.upload_url) throw new Error("The server did not return an upload URL.");

      // 2. Send the file straight to Cloudflare. Deliberately plain fetch/XHR, not the `api`
      //    instance — that one attaches our auth header and base URL, neither of which belongs
      //    on a third-party upload.
      const body = new FormData();
      body.append("file", file);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", slot.upload_url);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Cloudflare rejected the upload (${xhr.status}).`));
        xhr.onerror = () => reject(new Error("The upload failed. Check your connection."));
        xhr.send(body);
      });

      toast.success("Uploaded. Students are notified once Cloudflare finishes processing it.");
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.errors?.classroom_id?.[0] ||
          err?.response?.data?.message ||
          err.message ||
          "Could not upload the recording."
      );
    } finally {
      setUploadingFor(null);
      setProgress(0);
      pendingClassRef.current = null;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RecordingIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Class Recordings
          </Typography>
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When the moderator starts a group class, Jibri records to your Cloudflare R2 bucket and the
        file appears here after the meeting ends. You can also upload a file manually if needed.
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={onFileChosen}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={100} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={3}>
          {uploadable.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Finished classes without a recording
              </Typography>
              <Stack spacing={1.5}>
                {uploadable.map((c) => (
                  <Card key={c.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ sm: "center" }}
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={700}>{c.name}</Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={c.group.name} />
                            {c.subject?.name && (
                              <Chip size="small" variant="outlined" label={c.subject.name} />
                            )}
                            {c.end_time && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={new Date(c.end_time).toLocaleString()}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box sx={{ minWidth: 190 }}>
                          <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => pickFile(c)}
                            disabled={uploadingFor !== null}
                            fullWidth
                          >
                            {uploadingFor === c.id ? `Uploading ${progress}%` : "Upload recording"}
                          </Button>
                          {uploadingFor === c.id && (
                            <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Recordings
            </Typography>
            {recordings.length === 0 ? (
              <Alert severity="info">
                No recordings yet. Start a group class as moderator (auto-saves to Cloudflare), or upload a file above.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {recordings.map((r) => {
                  const chip = statusChip[r.status] || statusChip.pending;
                  const duration = formatDuration(r.duration_seconds);
                  return (
                    <Card
                      key={r.id}
                      variant="outlined"
                      sx={{ borderRadius: 2, borderLeft: `4px solid ${chip.color}` }}
                    >
                      <CardContent>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={chip.label}
                            sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }}
                          />
                          {r.source === "cloudflare_live" && (
                            <Chip size="small" variant="outlined" label="Auto (Cloudflare Stream)" />
                          )}
                          {r.source === "jibri_r2" && (
                            <Chip size="small" variant="outlined" label="Auto (Jibri → R2)" />
                          )}
                          {r.source === "upload" && (
                            <Chip size="small" variant="outlined" label="Uploaded" />
                          )}
                          {r.group?.name && <Chip size="small" variant="outlined" label={r.group.name} />}
                          {duration && <Chip size="small" variant="outlined" label={duration} />}
                          {r.available_until && (
                            <Tooltip title="Recordings expire with the group">
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Until ${new Date(r.available_until).toLocaleDateString()}`}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                        <Typography fontWeight={700}>{r.title || r.class?.name}</Typography>
                        {r.recorded_at && (
                          <Typography variant="caption" color="text.secondary">
                            Recorded {new Date(r.recorded_at).toLocaleString()}
                          </Typography>
                        )}
                        {(r.status === "uploading" || r.status === "processing") && (
                          <LinearProgress sx={{ mt: 1.5 }} />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
